import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, Zap } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const PERIODS = [
  { key: 7, label: "7D" },
  { key: 14, label: "14D" },
  { key: 30, label: "30D" },
];

const TIER_COLORS = {
  bounce: "bg-slate-300",
  casual: "bg-sky-400",
  engaged: "bg-violet-500",
  power: "bg-amber-500",
};

const ENTRY_LABELS = {
  login: "Login",
  offer_view: "Offer View",
  page_view: "Page View",
  search: "Search",
  favorite: "Favorite",
  claim: "Claim",
  unknown: "Other",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
      <p className="font-semibold">{label}</p>
      <p>{payload[0].value} sessions</p>
    </div>
  );
};

export default function SessionInsightsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/session-insights?days=${days}`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Failed to load</p>;

  const { summary, quality_distribution, daily_sessions, top_entry_points } = data;

  return (
    <div className="space-y-4" data-testid="session-insights-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-100">
          <p className="text-lg font-bold text-indigo-700">{summary.total_sessions}</p>
          <p className="text-[10px] text-indigo-500 font-medium">Sessions</p>
        </div>
        <div className="bg-sky-50 rounded-xl p-3 text-center border border-sky-100">
          <p className="text-lg font-bold text-sky-700">{summary.avg_actions_per_session}</p>
          <p className="text-[10px] text-sky-500 font-medium">Avg Actions</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-lg font-bold text-amber-700">{summary.avg_session_minutes}m</p>
          <p className="text-[10px] text-amber-500 font-medium">Avg Duration</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className={`rounded-xl p-3 text-center border ${summary.bounce_rate > 60 ? "bg-red-50 border-red-100" : "bg-emerald-50 border-emerald-100"}`}>
          <p className={`text-lg font-bold ${summary.bounce_rate > 60 ? "text-red-700" : "text-emerald-700"}`}>{summary.bounce_rate}%</p>
          <p className={`text-[10px] font-medium ${summary.bounce_rate > 60 ? "text-red-500" : "text-emerald-500"}`}>Bounce Rate</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
          <p className="text-lg font-bold text-emerald-700">{summary.conversion_rate}%</p>
          <p className="text-[10px] text-emerald-500 font-medium">Conversion Rate</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setDays(p.key)}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
              days === p.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
            }`}
            data-testid={`session-period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Session quality distribution */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-600 px-1">Session Quality</p>
        {quality_distribution.map((q) => (
          <div key={q.tier} className="flex items-center gap-3" data-testid={`quality-tier-${q.tier}`}>
            <span className="text-xs font-medium text-slate-600 w-28">{q.label}</span>
            <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${TIER_COLORS[q.tier]} transition-all duration-500`}
                style={{ width: `${Math.max(q.pct, 2)}%` }}
              />
            </div>
            <span className="text-[10px] font-semibold text-slate-500 w-8 text-right">{q.count}</span>
            <span className="text-[10px] text-slate-400 w-10 text-right">{q.pct}%</span>
          </div>
        ))}
      </div>

      {/* Daily sessions chart */}
      <div>
        <p className="text-xs font-semibold text-slate-600 px-1 mb-2">Sessions Over Time</p>
        <div style={{ height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily_sessions} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "#94a3b8" }}
                tickFormatter={(v) => { const p = v.split("-"); return `${p[1]}/${p[2]}`; }}
                axisLine={false} tickLine={false}
              />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="sessions" stroke="#6366f1" fill="url(#gSessions)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top entry points */}
      {top_entry_points.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-600 px-1">How Sessions Start</p>
          <div className="flex flex-wrap gap-2">
            {top_entry_points.map((ep) => (
              <div
                key={ep.event_type}
                className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5"
                data-testid={`entry-point-${ep.event_type}`}
              >
                <span className="text-xs font-semibold text-slate-700">
                  {ENTRY_LABELS[ep.event_type] || ep.event_type}
                </span>
                <span className="text-[10px] text-slate-400 ml-1.5">{ep.count} ({ep.pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
